/**
 * Правка плана агроцикла прямым действием на шкале (`WindowTimeline`).
 *
 * Что делает экран, пока шкала только сообщает намерение:
 * - работа без кампании: сдвиг сохраняется сразу (`PATCH …/works/{id}`), полоса
 *   встаёт на новое место ещё до ответа сервера, а внизу — «Сдвинуто · Отменить»;
 * - работа с идущей кампанией: срок меняет решение кампании, поэтому вместо
 *   сохранения открывается «Перенести» с датами, которые человек вытащил;
 * - нажатие на полосу — ящик работы; «+» у этапа — ящик новой работы;
 *   «Убрать из плана» — окно с причиной.
 *
 * Правки идут по одной (сервер считает цепочки зависимых работ): пока уходит
 * одна, следующая ждёт, а быстрые повторы сдвигов одной работы (стрелки с
 * клавиатуры) сливаются в одну. Каждая правка уходит с причиной — серверный
 * журнал плана остаётся полным.
 */

import { useCallback, useRef, useState, type ReactNode } from 'react';
import { ApiError } from '../../../api/client';
import { defineMessages } from '../../../i18n/define';
import { useT } from '../../../i18n/hooks';
import { dateShort, windowLabel } from '../../lib/format';
import { COMMON } from '../../../i18n/common';
import { FormActions, FormDialog, TextArea, toast, useFormDialogClose } from '../../ui/forms';
import type { ShiftInfo } from '../../ui/timeline/WindowTimeline';
import type { DayWindow } from '../../ui/timeline/windowMath';
import { DecisionDialog } from '../ops/campaigns/DecisionDialog';
import { AddWorkDrawer } from './AddWorkDrawer';
import { agrocycleApi, type AgrocycleDetail, type PlanStage } from './agrocycleApi';
import type { AgrocycleState } from './AgrocyclePlan';
import { PlanWorkDrawer, type WorkFacts } from './PlanWorkDrawer';
import { barKey, campaignOf, canReschedule, findWork, isLaunched, planShift, workIdOfBar, type CycleTiming } from './planEdit';

const m = defineMessages({
  reasonShift: { id: 'cycle.plan.reason.shift', defaultMessage: 'сдвиг сроков', description: 'Причина правки плана, которую приложение отправляет серверу; потом видна в журнале плана' },
  reasonUndo: { id: 'cycle.plan.reason.undo', defaultMessage: 'отмена сдвига', description: 'Причина правки плана, которую приложение отправляет серверу при «Отменить» после сдвига; потом видна в журнале плана' },
  failed: { id: 'cycle.plan.edit.failed', defaultMessage: 'Не получилось — повторите.', description: 'Ошибка правки плана агроцикла (менеджер), когда сервер не дал своего текста' },
  shifted: { id: 'cycle.plan.edit.shifted', defaultMessage: 'Сдвинуто: {name}, {window}', description: 'Уведомление после сдвига работы плана на шкале; {name} — работа, {window} — новое окно' },
  shiftedWith: {
    id: 'cycle.plan.edit.shiftedWith',
    defaultMessage: 'Сдвинуто: {name}, {window} · вместе с ней {count, plural, one {# работа} few {# работы} many {# работ} other {# работы}}',
    description: 'Уведомление после сдвига работы плана, от которой отсчитываются другие; {name} — работа, {window} — новое окно, {count} — сколько зависимых работ сдвинулось',
  },
  undone: { id: 'cycle.plan.edit.undone', defaultMessage: 'Возвращено: {name}', description: 'Уведомление после «Отменить» сдвиг работы плана; {name} — работа' },
  cannotShape: {
    id: 'cycle.plan.edit.cannotShape',
    defaultMessage: 'Так сдвинуть нельзя: такой срок не записать (29 февраля или год вне «предыдущий / посадки / следующий»).',
    description: 'Ошибка при сдвиге работы плана на шкале: срок не получается записать как правило',
  },
  cannotRange: {
    id: 'cycle.plan.edit.cannotRange',
    defaultMessage: 'Так сдвинуть нельзя: окна работ перестанут сходиться (конец окажется раньше начала).',
    description: 'Ошибка при сдвиге работы плана на шкале: расчёт окон не сходится',
  },
  rescheduled: { id: 'cycle.plan.edit.rescheduled', defaultMessage: 'Срок кампании перенесён: {name}', description: 'Уведомление после переноса срока кампании со шкалы плана; {name} — работа' },
  removeTitle: { id: 'cycle.plan.remove.title', defaultMessage: 'Убрать из плана: {name}', description: 'Заголовок окна «убрать работу из плана» (менеджер); {name} — работа' },
  removeConfirm: { id: 'cycle.plan.remove.confirm', defaultMessage: 'Убрать', description: 'Кнопка подтверждения «убрать работу из плана»' },
  removeHint: {
    id: 'cycle.plan.remove.hint',
    defaultMessage: 'Работа останется в плане зачёркнутой строкой, номер полива не переиспользуется. Причина попадёт в журнал плана.',
    description: 'Пояснение в окне «убрать работу из плана» (менеджер): что произойдёт',
  },
  removeReason: { id: 'cycle.plan.remove.reason', defaultMessage: 'Причина (обязательна)', description: 'Подпись поля причины в окне «убрать работу из плана»' },
  removeReasonRequired: { id: 'cycle.plan.remove.reasonRequired', defaultMessage: 'Укажите причину.', description: 'Ошибка в окне «убрать работу из плана»: причина пуста' },
  removed: { id: 'cycle.plan.remove.done', defaultMessage: 'Работа убрана из плана: {name}', description: 'Уведомление после того, как работа убрана из плана; {name} — работа' },
});

type Params = Record<string, string | number | boolean | null | undefined>;

interface Job {
  kind: 'shift' | 'undo';
  workId: number;
  name: string;
  timing: CycleTiming;
  /** Срок до правки — для «Отменить». */
  previous: CycleTiming;
  /** Окно работы после правки (для подписи) и окна, на которые вернётся «Отменить». */
  window: DayWindow;
  restore: Map<number, DayWindow>;
}

export interface PlanEditing {
  /** Окна, показанные до ответа сервера: ключ строки шкалы → окно. */
  overlay: Readonly<Record<string, DayWindow>>;
  selectedId: string | null;
  select: (barId: string | null) => void;
  onShift: (id: string, from: string, to: string | null, info: ShiftInfo) => void;
  previewShift: (id: string, from: string, to: string | null, info: ShiftInfo) => Record<string, DayWindow> | null;
  /** «+» у этапа: открыть ящик новой работы. */
  addTo: (stageId: number) => void;
  /** Ящики, окна решений и уведомление — один раз в разметке экрана. */
  node: ReactNode;
}

function rangeText(win: DayWindow): string {
  return win.to && win.to !== win.from ? windowLabel(win.from, win.to) : dateShort(win.from);
}

/** Этапы с учётом правок, которые ещё в пути: их сроки и окна поверх ответа сервера. */
function withPending(stages: ReadonlyArray<PlanStage>, timings: ReadonlyMap<number, CycleTiming>, windows: Readonly<Record<string, DayWindow>>): PlanStage[] {
  return stages.map((stage) => ({
    ...stage,
    works: stage.works.map((work) => {
      const timing = timings.get(work.id);
      const win = windows[barKey(work.id)];
      if (!timing && !win) return work;
      return { ...work, timing: timing ?? work.timing, window_from: win ? win.from : work.window_from, window_to: win ? win.to : work.window_to };
    }),
  }));
}

export function usePlanEditing({
  agro,
  editable,
  factsOf,
}: {
  agro: AgrocycleState | null;
  /** Менеджер правит план начатого цикла. */
  editable: boolean;
  /** Факты работы для ящика (готовые подписи). */
  factsOf: (workId: number) => WorkFacts | null;
}): PlanEditing {
  const t = useT();
  const detail = agro?.detail ?? null;
  const detailRef = useRef<AgrocycleDetail | null>(detail);
  detailRef.current = detail;
  const agroRef = useRef(agro);
  agroRef.current = agro;

  const [overlay, setOverlayState] = useState<Record<string, DayWindow>>({});
  const overlayRef = useRef(overlay);
  const setOverlay = useCallback((next: Record<string, DayWindow>) => {
    overlayRef.current = next;
    setOverlayState(next);
  }, []);
  const pendingTimings = useRef(new Map<number, CycleTiming>());
  const queue = useRef<Job[]>([]);
  const running = useRef(false);

  const [selectedId, setSelectedId] = useState<string | null>(null);
  const [addingStage, setAddingStage] = useState<number | null>(null);
  const [rescheduling, setRescheduling] = useState<{ workId: number; window: DayWindow } | null>(null);
  const [removing, setRemoving] = useState<number | null>(null);

  const errorText = (error: unknown): string => (error instanceof ApiError ? error.message : t(m.failed));

  const pump = useCallback(async () => {
    if (running.current) return;
    running.current = true;
    try {
      for (let job = queue.current.shift(); job; job = queue.current.shift()) {
        const current = detailRef.current;
        if (!current) break;
        try {
          const result = await agrocycleApi.patchWork(current.key, job.workId, { timing: job.timing, reason: t(job.kind === 'shift' ? m.reasonShift : m.reasonUndo) }, agroRef.current?.params ?? {});
          agroRef.current?.update(result.cycle);
          const moved = result.shifted.length;
          if (job.kind === 'undo') toast.show({ message: t(m.undone, { name: job.name }) });
          else
            toast.show({
              message: moved ? t(m.shiftedWith, { name: job.name, window: rangeText(job.window), count: moved }) : t(m.shifted, { name: job.name, window: rangeText(job.window) }),
              undo: { onUndo: () => submit({ kind: 'undo', workId: job.workId, name: job.name, timing: job.previous, previous: job.timing, window: job.window, restore: new Map() }, job.restore) },
            });
        } catch (error) {
          // Дальше стоящие правки строились на этой: бросаем их, полосы вернутся к ответу сервера.
          queue.current = [];
          toast.show({ message: errorText(error), tone: 'error' });
        }
      }
    } finally {
      running.current = false;
      pendingTimings.current.clear();
      setOverlay({});
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [setOverlay, t]);

  /** Поставить правку в очередь: быстрые повторы сдвига одной работы сливаются, «Отменить» первой берёт срок до всей серии. */
  const submit = (job: Job, show?: ReadonlyMap<number, DayWindow>) => {
    if (show) {
      const next = { ...overlayRef.current };
      for (const [id, win] of show) next[barKey(id)] = win;
      setOverlay(next);
    }
    pendingTimings.current.set(job.workId, job.timing);
    const waiting = queue.current.findIndex((j) => j.kind === 'shift' && j.workId === job.workId);
    if (job.kind === 'shift' && waiting >= 0) {
      const first = queue.current[waiting]!;
      queue.current[waiting] = { ...job, previous: first.previous, restore: first.restore };
    } else queue.current.push(job);
    void pump();
  };

  const planFor = (id: string, from: string, to: string | null, info: ShiftInfo) => {
    const current = detailRef.current;
    const workId = workIdOfBar(id);
    const found = current && workId !== null ? findWork(current.plan.stages, workId) : null;
    if (!current || !found) return null;
    const stages = withPending(current.plan.stages, pendingTimings.current, overlayRef.current);
    const result = planShift(stages, current.anchor_year, found.work.id, { from: info.prevFrom, to: info.prevTo }, { from, to }, info.kind);
    return { current, work: found.work, stages, result };
  };

  const onShift: PlanEditing['onShift'] = (id, from, to, info) => {
    const planned = planFor(id, from, to, info);
    if (!planned) return;
    const { work, stages, result } = planned;
    if (isLaunched(work)) {
      // Срок запущенной работы переносит решение кампании: шкала лишь собрала даты.
      if (canReschedule(work)) setRescheduling({ workId: work.id, window: { from, to } });
      return;
    }
    if (!result.ok) {
      toast.show({ message: t(result.reason === 'shape' ? m.cannotShape : m.cannotRange), tone: 'error' });
      return;
    }
    const restore = new Map<number, DayWindow>();
    for (const stage of stages)
      for (const w of stage.works) if (result.plan.windows.has(w.id) && w.window_from) restore.set(w.id, { from: w.window_from, to: w.window_to });
    submit(
      { kind: 'shift', workId: work.id, name: work.label, timing: result.plan.timing, previous: result.plan.previous, window: { from, to }, restore },
      result.plan.windows,
    );
  };

  const previewShift: PlanEditing['previewShift'] = (id, from, to, info) => {
    const planned = planFor(id, from, to, info);
    if (!planned || isLaunched(planned.work) || !planned.result.ok) return null;
    const out: Record<string, DayWindow> = {};
    for (const [workId, win] of planned.result.plan.windows) if (workId !== planned.work.id) out[barKey(workId)] = win;
    return Object.keys(out).length > 0 ? out : null;
  };

  /* ---- ящики и окна */
  const selected = selectedId !== null && detail ? findWork(detail.plan.stages, workIdOfBar(selectedId) ?? -1) : null;
  const adding = addingStage !== null && detail ? detail.plan.stages.find((s) => s.id === addingStage) ?? null : null;
  const resched = rescheduling && detail ? findWork(detail.plan.stages, rescheduling.workId) : null;
  const removal = removing !== null && detail ? findWork(detail.plan.stages, removing) : null;

  const saved = (cycle: AgrocycleDetail, message: string) => {
    agro?.update(cycle);
    setSelectedId(null);
    setAddingStage(null);
    toast.show({ message });
  };

  const node = (
    <>
      {selected && detail ? (
        <PlanWorkDrawer
          key={selected.work.id}
          cycle={detail}
          work={selected.work}
          facts={factsOf(selected.work.id) ?? { stage: selected.stage.name, window: '', state: '', progress: null, href: null, tags: [] }}
          canEdit={editable}
          params={agro?.params ?? {}}
          onClose={() => setSelectedId(null)}
          onSaved={saved}
          onReschedule={() => {
            setSelectedId(null);
            setRescheduling({ workId: selected.work.id, window: { from: selected.work.window_from ?? '', to: selected.work.window_to } });
          }}
          onRemove={() => {
            setSelectedId(null);
            setRemoving(selected.work.id);
          }}
        />
      ) : null}
      {adding && detail ? <AddWorkDrawer cycle={detail} stage={adding} params={agro?.params ?? {}} onClose={() => setAddingStage(null)} onAdded={saved} /> : null}
      {resched && rescheduling ? (
        <DecisionDialog
          kind="reschedule"
          campaign={campaignOf(resched.work, rescheduling.window)}
          onClose={() => setRescheduling(null)}
          onDone={() => {
            setRescheduling(null);
            agro?.req.reload();
            toast.show({ message: t(m.rescheduled, { name: resched.work.label }) });
          }}
        />
      ) : null}
      {removal && detail ? (
        <RemoveDialog
          cycle={detail}
          workId={removal.work.id}
          name={removal.work.label}
          params={agro?.params ?? {}}
          onClose={() => setRemoving(null)}
          onRemoved={(cycle) => {
            agro?.update(cycle);
            setRemoving(null);
            toast.show({ message: t(m.removed, { name: removal.work.label }) });
          }}
        />
      ) : null}
    </>
  );

  return { overlay, selectedId, select: setSelectedId, onShift, previewShift, addTo: setAddingStage, node };
}

/** «Убрать из плана»: причина обязательна, попадает в журнал плана. */
function RemoveDialog({ cycle, workId, name, params, onClose, onRemoved }: { cycle: AgrocycleDetail; workId: number; name: string; params: Params; onClose: () => void; onRemoved: (cycle: AgrocycleDetail) => void }) {
  const t = useT();
  const [reason, setReason] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const confirm = async () => {
    if (busy) return;
    if (!reason.trim()) {
      setError(t(m.removeReasonRequired));
      return;
    }
    setBusy(true);
    setError(null);
    try {
      const result = await agrocycleApi.removeWork(cycle.key, workId, reason.trim(), params);
      onRemoved(result.cycle);
    } catch (e) {
      setError(e instanceof ApiError ? e.message : t(m.failed));
      setBusy(false);
    }
  };
  return (
    <FormDialog title={t(m.removeTitle, { name })} size="sm" isOpen onClose={onClose} isDirty={reason.trim() !== ''} error={error} footer={<RemoveActions busy={busy} onConfirm={() => void confirm()} />}>
      <p className="agc-muted">{t(m.removeHint)}</p>
      <TextArea
        label={t(m.removeReason)}
        value={reason}
        onChange={(next) => {
          setReason(next);
          setError(null);
        }}
        minRows={2}
        isRequired
      />
    </FormDialog>
  );
}

function RemoveActions({ busy, onConfirm }: { busy: boolean; onConfirm: () => void }) {
  const t = useT();
  const close = useFormDialogClose();
  return <FormActions secondary={{ label: t(COMMON.cancel), onPress: close }} primary={{ label: t(m.removeConfirm), isPending: busy, onPress: onConfirm, tone: 'danger' }} />;
}
